import { useVenues } from './useVenues';

export const useRoleAccess = () => {
  const { activeMemberRole, activeMemberPermissions = {}, activeVenue, profile, organizationId, orgRole, venues = [] } = useVenues();

  const rawTier = (activeVenue?.tier || profile?.tier || 'STARTER').toUpperCase();
  const tier = (['STARTER', 'PRO', 'MAX', 'ENTERPRISE'].includes(rawTier) ? rawTier : 'STARTER') as 'STARTER' | 'PRO' | 'MAX' | 'ENTERPRISE';

  // Basic role flags
  const isOwner = activeMemberRole === 'OWNER';
  const isManager = activeMemberRole === 'MANAGER';
  const isChef = activeMemberRole === 'CHEF';
  const isWaiter = activeMemberRole === 'WAITER';
  const isViewer = activeMemberRole === 'VIEWER';

  // Grouped permissions
  const isManagement = isOwner || isManager;
  const isStaff = isChef || isWaiter;

  // Tier flags
  const isProPlus = ['PRO', 'MAX', 'ENTERPRISE'].includes(tier);
  const isMaxPlus = ['MAX', 'ENTERPRISE'].includes(tier);
  const isEnterprise = tier === 'ENTERPRISE';

  // Granular settings
  const allowViewerManagement = activeVenue?.theme_settings?.allow_viewer_management ?? false;

  // Advanced Team Permissions (Custom Permissions on MAX & Custom Role Schemas on Enterprise)
  const canAccessCustomPermissions = isMaxPlus;
  const canAccessCustomRoleSchemas = isEnterprise;
  const hasCustomOverrides = isMaxPlus && activeMemberPermissions && Object.keys(activeMemberPermissions).length > 0;

  // Granular capability resolutions
  const canEditMenu = isOwner ? true : (hasCustomOverrides && typeof activeMemberPermissions.can_edit_menu === 'boolean' ? activeMemberPermissions.can_edit_menu : isManagement);
  const canViewAnalytics = isOwner ? true : (hasCustomOverrides && typeof activeMemberPermissions.can_view_analytics === 'boolean' ? activeMemberPermissions.can_view_analytics : (isManagement || (isViewer && allowViewerManagement)));
  const canManageKitchen = isOwner ? true : (hasCustomOverrides && typeof activeMemberPermissions.can_manage_kitchen === 'boolean' ? activeMemberPermissions.can_manage_kitchen : (isManagement || isChef));
  const canManageWaiters = isOwner ? true : (hasCustomOverrides && typeof activeMemberPermissions.can_manage_waiters === 'boolean' ? activeMemberPermissions.can_manage_waiters : (isManagement || isWaiter));
  const canManageMarketing = isOwner ? true : (hasCustomOverrides && typeof activeMemberPermissions.can_manage_marketing === 'boolean' ? activeMemberPermissions.can_manage_marketing : isManagement);
  const canAccessSettings = isOwner ? true : (hasCustomOverrides && typeof activeMemberPermissions.can_manage_settings === 'boolean' ? activeMemberPermissions.can_manage_settings : (isManagement || (isViewer && allowViewerManagement)));
  const canManageTeam = isOwner ? true : (hasCustomOverrides && typeof activeMemberPermissions.can_manage_team === 'boolean' ? activeMemberPermissions.can_manage_team : isManagement);
  const canManageOrders = isOwner ? true : (hasCustomOverrides && typeof activeMemberPermissions.can_manage_orders === 'boolean' ? activeMemberPermissions.can_manage_orders : (isManagement || isStaff));

  return {
    role: activeMemberRole,
    permissions: activeMemberPermissions,
    organizationId,
    orgRole,
    tier,
    isOwner,
    isManager,
    isChef,
    isWaiter,
    isViewer,
    isManagement,
    isStaff,

    // Tier-based feature flags
    isProPlus,
    isMaxPlus,
    isEnterprise,
    canAccessCustomPermissions,
    canAccessCustomRoleSchemas,
    // Reservations workspace is strictly gated to MAX and ENTERPRISE tiers
    canAccessReservations: isMaxPlus,
    canAccessLoyalty: isMaxPlus,
    canAccessAuditLogs: isMaxPlus,
    canAccessCustomDomains: isMaxPlus,
    // Customer Feedback & Review Insights is strictly gated to MAX and ENTERPRISE tiers
    canAccessReviewInsights: isMaxPlus,
    canAccessAdvancedThemes: isProPlus,
    canAccessKitchenOps: isProPlus,
    canAccessSignage: isProPlus,
    canAccessServiceHours: isProPlus,
    canAccessDepartments: isProPlus,
    // Category management is available across all tiers for baseline menu organization
    canAccessCategories: true,
    // Independent Google SEO Suite is strictly gated to MAX and ENTERPRISE tiers (tied to custom domain)
    canAccessSEO: isMaxPlus,
    canAccessMultiVenue: isMaxPlus,
    canAccessMultiLanguage: isProPlus,
    canAccessWhiteLabel: isProPlus,
    canAccessSSO: isEnterprise,
    canAccessAPI: isEnterprise,
    canAccess3DAtmos: isEnterprise,
    canAccessStaffDash: isProPlus,

    // Permission checks (respecting granular custom overrides when active)
    canManageVenue: isManagement,
    canEditMenu,
    canViewAnalytics,
    canAccessSettings,
    canManageTeam,
    canManageKitchen,
    canManageWaiters,
    canManageMarketing,
    canManageOrders,

    // For disabling UI elements
    readOnly: !canEditMenu,
    tierLimits: {
      // Aligned with TIER_LIMITS in BillingView.tsx
      teamSize: tier === 'STARTER' ? 1 : tier === 'PRO' ? 5 : Infinity,
      venueCount: tier === 'STARTER' ? 1 : tier === 'PRO' ? 3 : tier === 'MAX' ? 10 : Infinity,
      languageCount: tier === 'STARTER' ? 1 : Infinity,
    },
  };
};
